import React, { useRef, useState } from 'react';
import {
  Alert,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import colors from '../../assets/theme/colors';
import {
  saveFileCareSourceForProcessing,
  saveImageCareSourceFileForProcessing,
  uploadOriginalFileToStorage,
} from './careSourceCaptureSubmitters';
import { rememberCareSourceActivityFromResult } from './careSourceSessionActivity';
import {
  isImageFile,
  prepareCareSourceFileForUpload,
  validateCareSourceFileSize,
} from './textExtraction/careSourceFileTextExtraction';

const DEFAULT_ACCEPTED_FILES = '.pdf,.txt,.csv,.tsv,.xls,.xlsx,.docx,image/*';

const CareSourceFileUploadButton = ({
  childId = '',
  onEnsureChildId,
  sourceContext = '',
  sourceType = 'file_import',
  label = 'Upload source',
  icon = null,
  showIcon = true,
  helperText = '',
  disabledText = '',
  acceptedFiles = DEFAULT_ACCEPTED_FILES,
  capture = '',
  disabled = false,
  onSaved,
  onError,
  variantStyle = '',
  buttonSx = {},
  helperTextSx = {},
  dropZone = false,
  profileChapterId = '',
  profileField = '',
  profilePlacementMode = '',
  selectedDate = null,
  selectedLocalDate = null,
  sourceUseCase = '',
  reviewPolicy = '',
}) => {
  const inputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageSeverity, setMessageSeverity] = useState('success');
  const [errorMessage, setErrorMessage] = useState('');
  const isDocumentStyle = variantStyle === 'document';

  const resetInput = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const saveSelectedFile = async (file) => {
    if (!file) {
      return;
    }

    setIsUploading(true);
    setMessage('');
    setMessageSeverity('success');
    setErrorMessage('');

    try {
      let targetChildId = childId;

      if (!targetChildId && onEnsureChildId) {
        const createdChild = await onEnsureChildId();
        targetChildId = createdChild?.id || createdChild || '';
      }

      if (!targetChildId) {
        throw new Error(disabledText || 'Add the basic profile details first, then upload the document.');
      }

      validateCareSourceFileSize(file);

      const fileExtension = String(file.name || '').toLowerCase().split('.').pop();
      const isImage = isImageFile(fileExtension, String(file.type || '').toLowerCase());
      const originalFileAttachment = isImage
        ? null
        : await uploadOriginalFileToStorage({ file, childId: targetChildId });
      const preparedSource = await prepareCareSourceFileForUpload(file);

      const result = preparedSource.documentType === 'image'
        ? await saveImageCareSourceFileForProcessing({
          childId: targetChildId,
          file,
          sourceType,
          sourceContext,
          profileChapterId,
          profileField,
          profilePlacementMode,
          selectedDate,
          selectedLocalDate,
          sourceUseCase,
          reviewPolicy,
        })
        : await saveFileCareSourceForProcessing({
          childId: targetChildId,
          rawText: preparedSource.rawText,
          sourceType,
          sourceContext,
          profileChapterId,
          profileField,
          profilePlacementMode,
          textExtractionStatus: preparedSource.textExtractionStatus,
          extractionError: preparedSource.extractionError,
          file,
          originalFileAttachment,
          selectedDate,
          selectedLocalDate,
          sourceUseCase,
          reviewPolicy,
        });

      rememberCareSourceActivityFromResult(result);
      setMessageSeverity(result?.duplicate ? 'info' : 'success');
      setMessage(
        result?.message
        || preparedSource.userMessage
        || 'Source saved.'
      );
      onSaved?.(result, { file });
    } catch (error) {
      const nextMessage = error?.message || 'We could not save this source right now.';
      setErrorMessage(nextMessage);
      onError?.(error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0] || null;
    await saveSelectedFile(file);
    resetInput();
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    if (disabled || isUploading) return;
    await saveSelectedFile(event.dataTransfer?.files?.[0] || null);
  };

  return (
    <Stack
      spacing={0.8}
      alignItems={dropZone ? "center" : "flex-start"}
      onDragOver={dropZone ? (event) => event.preventDefault() : undefined}
      onDrop={dropZone ? handleDrop : undefined}
      sx={dropZone ? {
        width: '100%',
        minHeight: 190,
        justifyContent: 'center',
        border: `2px dashed ${colors.brand.tint}`,
        borderRadius: 3,
        bgcolor: '#FFFFFF',
        p: 3,
      } : undefined}
    >
      <input
        ref={inputRef}
        type="file"
        accept={acceptedFiles}
        capture={capture || undefined}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <Button
        type="button"
        variant="outlined"
        startIcon={showIcon ? (icon || <UploadFileRoundedIcon />) : undefined}
        disabled={disabled || isUploading || (!childId && !onEnsureChildId)}
        onClick={() => inputRef.current?.click()}
        sx={{
          width: isDocumentStyle ? '100%' : 'auto',
          minHeight: isDocumentStyle ? 52 : 'auto',
          borderRadius: isDocumentStyle ? 2.2 : '999px',
          px: isDocumentStyle ? 1.7 : 1.7,
          py: isDocumentStyle ? 1.05 : 0.85,
          borderColor: colors.brand.tint,
          bgcolor: isDocumentStyle ? colors.semantic.profileFlow.nudgeBg : 'transparent',
          color: colors.brand.deep,
          fontWeight: 900,
          fontSize: isDocumentStyle ? '0.92rem' : undefined,
          textTransform: 'none',
          '&:hover': {
            borderColor: colors.brand.deep,
            bgcolor: colors.semantic.profileFlow.nudgeBg,
          },
          ...buttonSx,
        }}
      >
        {isUploading ? 'Saving source...' : label}
      </Button>
      {helperText ? (
        <Typography sx={{
          width: isDocumentStyle ? '100%' : 'auto',
          textAlign: isDocumentStyle ? 'center' : 'left',
          fontSize: '0.78rem',
          color: colors.semantic.ink2,
          lineHeight: 1.4,
          ...helperTextSx,
        }}>
          {helperText}
        </Typography>
      ) : null}
      {disabled && disabledText ? (
        <Typography sx={{ fontSize: '0.78rem', color: colors.semantic.ink2, lineHeight: 1.4 }}>
          {disabledText}
        </Typography>
      ) : null}
      {message ? (
        <Alert severity={messageSeverity} sx={{ py: 0.3 }}>
          {message}
        </Alert>
      ) : null}
      {errorMessage ? (
        <Alert severity="warning" sx={{ py: 0.3 }}>
          {errorMessage}
        </Alert>
      ) : null}
    </Stack>
  );
};

export default CareSourceFileUploadButton;
